/**
 * ProcurementAnalytics — 6-dashboard drill-down surface for the Procurement
 * & Supply-Chain Schedule module.  Every chart, bar, KPI tile, table row and
 * heat-map cell is clickable and opens a right-side slide-over sheet with a
 * filtered list of underlying MCIs / POs / GRNs — plus a CSV export of the
 * filtered slice and a deep-link back to the source tab.
 */
import { useCallback, useEffect, useMemo, useState, Fragment, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Loader2, TrendingUp, Clock, Package, DollarSign, ClipboardCheck, AlertTriangle,
  ChevronRight, RefreshCw, X, ExternalLink, Download, Users, Sparkles, FileSpreadsheet,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const C = {
  primary: '#1E4E8C', primaryText: 'var(--tone-navy-text)', accent: '#F26B1F', success: '#10B981', warn: '#F59E0B',
  danger: '#EF4444', slate: 'var(--text-primary)', slateMuted: 'var(--text-muted)', border: 'var(--border-color)',
  bg: 'var(--tone-slate-bg)',
};

const fmtNum = (n) => n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
const fmtMoney = (n) => n == null ? '—' :
  Math.abs(n) >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` :
  Math.abs(n) >= 1e3 ? `$${(n / 1e3).toFixed(1)}k` : `$${Math.round(n)}`;
const fmtDate = (s) => !s ? '—' : String(s).slice(0, 10);

/* --------------------------------------------------------------- helpers */

const Kpi = ({ icon: Icon, label, value, color, onClick, testId }) => (
  <div className={`p-2.5 rounded-lg border bg-[var(--card-bg)] transition-all ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-blue-400' : ''}`}
    style={{ borderColor: C.border }} onClick={onClick} data-testid={testId}>
    <div className="flex items-center gap-1">
      <Icon className="w-3.5 h-3.5" style={{ color }} />
      <div className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>{label}</div>
    </div>
    <div className="text-lg font-bold font-mono mt-0.5" style={{ color }}>{value}</div>
  </div>
);

const Section = ({ icon: Icon, title, hint, right, children, testId }) => (
  <div className="rounded-lg border bg-[var(--card-bg)] p-3" style={{ borderColor: C.border }} data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4" style={{ color: C.primaryText }} />
      <div className="text-sm font-bold" style={{ color: C.slate }}>{title}</div>
      {hint && <div className="text-[10px] ml-2" style={{ color: C.slateMuted }}>{hint}</div>}
      {right && <div className="ml-auto">{right}</div>}
    </div>
    {children}
  </div>
);

const Bar = ({ pct, color = C.primary, label }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 relative rounded-full overflow-hidden" style={{ height: 8, background: 'var(--border-color)' }}>
      <div className="absolute inset-y-0 left-0 rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
    </div>
    <div className="text-[10px] font-mono font-bold" style={{ color, minWidth: 88, textAlign: 'right' }}>{label}</div>
  </div>
);

const csvDownload = (name, rows) => {
  if (!rows || !rows.length) { toast.error('Nothing to export'); return; }
  const keys = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => esc(r[k])).join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a'); a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
  toast.success(`${rows.length} row(s) exported`);
};

/* --------------------------------------------------- drilldown slide-over */

function DrilldownSheet({ open, onClose, projectId, spec }) {
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [subVendor, setSubVendor] = useState(null);
  const [subVendorData, setSubVendorData] = useState(null);
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const [ai, setAi] = useState(null); // null | 'loading' | { analysis, source }

  const fetchPayload = useCallback(async () => {
    if (!spec) return;
    setLoading(true); setPayload(null);
    try {
      const { data } = await api.get(spec.url);
      setPayload(data);
    } catch (e) { toast.error('Failed to load drilldown'); }
    setLoading(false);
  }, [spec]);

  useEffect(() => { if (open) fetchPayload(); }, [open, fetchPayload]);
  useEffect(() => { if (!open) { setAi(null); setXlsxBusy(false); } }, [open]);
  useEffect(() => {
    if (!subVendor) { setSubVendorData(null); return; }
    (async () => {
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/mci/analytics/vendor-summary?vendor=${encodeURIComponent(subVendor)}&category=${spec?.category || 'BULK'}`);
        setSubVendorData(data);
      } catch { toast.error('Failed to load vendor summary'); }
    })();
  }, [subVendor, projectId, spec]);

  // Serialize the visible rows into headers[]/rows[][] for xlsx + AI prompt
  const serialize = useCallback(() => {
    const rowsList = payload?.[spec?.rowsKey || 'mcis'] || [];
    if (!rowsList.length) return { headers: [], rows: [] };
    const shape = spec?.shape;
    const cols = shape === 'po' ? [
      ['poNumber', 'PO#'], ['vendor', 'Vendor'], ['discipline', 'Discipline'],
      ['poDate', 'PO Date'], ['requiredAtSiteDate', 'Req@Site'], ['grnDate', 'GRN Date'],
      ['leadTimeDays', 'Lead (d)'], ['poValue', 'PO Value'], ['mciCount', 'MCIs'],
      ['state', 'State'],
    ] : shape === 'grn' ? [
      ['receiptNumber', 'GRN#'], ['receiptDate', 'Date'], ['mciLineNumber', 'MCI#'],
      ['materialDescription', 'Material'], ['vendor', 'Vendor'], ['poNumber', 'PO#'],
      ['acceptedQty', 'Accepted'], ['iqcQty', 'Pending'], ['rejectedQty', 'Rejected'],
      ['rejectionReasons', 'Reasons'],
    ] : [
      ['mciLineNumber', 'MCI#'], ['materialDescription', 'Material'], ['vendor', 'Vendor'],
      ['discipline', 'Discipline'], ['poNumber', 'PO#'], ['quantity', 'Qty'], ['unit', 'Unit'],
      ['poValue', 'PO Value'], ['requiredAtSiteDate', 'Req@Site'], ['ageDays', 'Age (d)'],
      ['progressPct', 'Progress %'], ['phase', 'Phase'],
    ];
    const headers = cols.map(c => c[1]);
    const rows = rowsList.map(r => cols.map(([k]) => {
      const v = r[k];
      if (Array.isArray(v)) return v.map(x => (typeof x === 'string' ? x : x?.code || '')).filter(Boolean).join('; ');
      return v == null ? '' : v;
    }));
    return { headers, rows, rowsList };
  }, [payload, spec]);

  const xlsxDownload = async () => {
    const { headers, rows, rowsList } = serialize();
    if (!rows.length) { toast.error('Nothing to export'); return; }
    setXlsxBusy(true);
    try {
      const res = await api.post(
        `/v1/projects/${projectId}/mci/analytics/drill-export.xlsx`,
        {
          title: spec.title,
          subtitle: spec.subtitle,
          headers, rows,
          exportName: spec.exportName || 'drilldown',
        },
        { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${spec.exportName || 'drilldown'}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`${rowsList.length} row(s) exported to xlsx`);
    } catch { toast.error('xlsx export failed'); }
    setXlsxBusy(false);
  };

  const explainDrill = async () => {
    const { rowsList } = serialize();
    if (!rowsList?.length) { toast.error('Nothing to analyse'); return; }
    setAi('loading');
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/mci/analytics/explain-drill`,
        {
          title: spec.title,
          subtitle: spec.subtitle,
          kind: spec.shape || 'mci',
          rows: rowsList.slice(0, 40),
        });
      setAi(data);
    } catch (e) {
      toast.error('Cognara explain failed');
      setAi(null);
    }
  };

  if (!open || !spec) return null;

  const rows = payload?.[spec.rowsKey || 'mcis'] || [];
  const totalCount = payload?.count ?? rows.length;

  const openInTab = (tabId) => {
    // Fire an event the ProcurementSchedule page can listen to.
    window.dispatchEvent(new CustomEvent('procurement.setTab', { detail: { tab: tabId } }));
    onClose();
  };

  const RowMCI = ({ r }) => (
    <tr style={{ borderTop: `1px solid ${C.border}` }} className="hover:bg-slate-50">
      <td className="p-1.5 font-mono font-bold" style={{ color: C.primaryText }}>{r.mciLineNumber}</td>
      <td className="p-1.5">{r.materialDescription}</td>
      <td className="p-1.5">
        <button className="underline decoration-dotted" onClick={() => setSubVendor(r.vendor)}>{r.vendor || '—'}</button>
      </td>
      <td className="p-1.5">{r.discipline || '—'}</td>
      <td className="p-1.5 font-mono">{r.poNumber || '—'}</td>
      <td className="p-1.5 text-right font-mono">{r.quantity != null ? `${r.quantity} ${r.unit || ''}` : '—'}</td>
      <td className="p-1.5 text-right font-mono">{fmtMoney(r.poValue)}</td>
      <td className="p-1.5">{fmtDate(r.requiredAtSiteDate)}</td>
      <td className="p-1.5 text-right font-mono" style={{ color: (r.ageDays || 0) > 60 ? C.danger : C.slate }}>
        {r.ageDays != null ? `${r.ageDays}d` : '—'}
      </td>
      <td className="p-1.5 text-right font-mono">{r.progressPct != null ? `${r.progressPct}%` : '—'}</td>
      <td className="p-1.5">
        <span className="px-1.5 rounded text-[9px] font-bold" style={{
          background: r.phase === 'COMPLETED' ? 'var(--tone-green-bg)' : r.phase === 'POST-ORDER' ? 'var(--tone-blue-bg)' : 'var(--tone-amber-bg)',
          color: r.phase === 'COMPLETED' ? 'var(--tone-green-text)' : r.phase === 'POST-ORDER' ? 'var(--tone-blue-text)' : 'var(--tone-amber-text)',
        }}>{r.phase || '—'}</span>
      </td>
    </tr>
  );

  const RowPO = ({ r }) => (
    <tr style={{ borderTop: `1px solid ${C.border}` }} className="hover:bg-slate-50">
      <td className="p-1.5 font-mono font-bold" style={{ color: C.primaryText }}>{r.poNumber}</td>
      <td className="p-1.5">
        <button className="underline decoration-dotted" onClick={() => setSubVendor(r.vendor)}>{r.vendor || '—'}</button>
      </td>
      <td className="p-1.5">{r.discipline || '—'}</td>
      <td className="p-1.5">{fmtDate(r.poDate)}</td>
      <td className="p-1.5">{fmtDate(r.requiredAtSiteDate)}</td>
      <td className="p-1.5">{fmtDate(r.grnDate)}</td>
      <td className="p-1.5 text-right font-mono" style={{ color: (r.leadTimeDays || 0) > 120 ? C.danger : C.slate }}>
        {r.leadTimeDays != null ? `${r.leadTimeDays}d` : '—'}
      </td>
      <td className="p-1.5 text-right font-mono">{fmtMoney(r.poValue)}</td>
      <td className="p-1.5 text-right font-mono">{r.mciCount}</td>
      <td className="p-1.5">
        <span className="px-1.5 rounded text-[9px] font-bold" style={{
          background: r.state === 'ON-TIME' ? 'var(--tone-green-bg)' : r.state === 'LATE' ? 'var(--tone-red-bg)' : 'var(--tone-amber-bg)',
          color: r.state === 'ON-TIME' ? 'var(--tone-green-text)' : r.state === 'LATE' ? 'var(--tone-red-text)' : 'var(--tone-amber-text)',
        }}>{r.state}</span>
      </td>
    </tr>
  );

  const RowGRN = ({ r }) => (
    <tr style={{ borderTop: `1px solid ${C.border}` }} className="hover:bg-slate-50">
      <td className="p-1.5 font-mono font-bold" style={{ color: C.primaryText }}>{r.receiptNumber || '—'}</td>
      <td className="p-1.5">{fmtDate(r.receiptDate)}</td>
      <td className="p-1.5 font-mono">{r.mciLineNumber}</td>
      <td className="p-1.5">{r.materialDescription || '—'}</td>
      <td className="p-1.5">
        <button className="underline decoration-dotted" onClick={() => setSubVendor(r.vendor)}>{r.vendor || '—'}</button>
      </td>
      <td className="p-1.5 font-mono">{r.poNumber || '—'}</td>
      <td className="p-1.5 text-right font-mono" style={{ color: C.success }}>{fmtNum(r.acceptedQty)}</td>
      <td className="p-1.5 text-right font-mono" style={{ color: C.warn }}>{fmtNum(r.iqcQty)}</td>
      <td className="p-1.5 text-right font-mono" style={{ color: C.danger }}>{fmtNum(r.rejectedQty)}</td>
      <td className="p-1.5 text-[10px]" style={{ color: C.slateMuted }}>
        {(r.rejectionReasons || []).map(x => (typeof x === 'string' ? x : x?.code)).filter(Boolean).join(', ') || '—'}
      </td>
    </tr>
  );

  const headers = spec.shape === 'po' ? (
    <tr>
      {['PO#','Vendor','Discipline','PO Date','Req@Site','GRN Date','Lead','Value','MCIs','State'].map(h =>
        <th key={h} className="p-1.5 text-left" style={{ color: C.slateMuted }}>{h}</th>)}
    </tr>
  ) : spec.shape === 'grn' ? (
    <tr>
      {['GRN#','Date','MCI#','Material','Vendor','PO#','Accepted','Pending','Rejected','Reasons'].map(h =>
        <th key={h} className="p-1.5 text-left" style={{ color: C.slateMuted }}>{h}</th>)}
    </tr>
  ) : (
    <tr>
      {['MCI#','Material','Vendor','Discipline','PO#','Qty','PO Value','Req@Site','Age','Progress','Phase'].map(h =>
        <th key={h} className="p-1.5 text-left" style={{ color: C.slateMuted }}>{h}</th>)}
    </tr>
  );

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-50" style={{ background: 'rgba(15,23,42,0.55)' }} onClick={onClose} />
      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-50 bg-[var(--card-bg)] shadow-2xl flex flex-col"
        style={{ width: 'min(1100px, 92vw)' }} data-testid="pa-drilldown-sheet">
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-start justify-between" style={{ borderColor: C.border }}>
          <div className="min-w-0">
            <div className="text-[10px] uppercase font-bold tracking-wide" style={{ color: C.slateMuted }}>{spec.eyebrow}</div>
            <div className="text-base font-bold truncate" style={{ color: C.slate }}>{spec.title}</div>
            {spec.subtitle && <div className="text-[11px] mt-0.5" style={{ color: C.slateMuted }}>{spec.subtitle}</div>}
            {payload && <div className="text-[10px] mt-1 font-mono" style={{ color: C.primaryText }}>
              {totalCount} record(s)
            </div>}
          </div>
          <div className="flex items-center gap-1">
            {spec.deepLinkTab && (
              <Button size="sm" variant="outline" onClick={() => openInTab(spec.deepLinkTab)}
                data-testid="pa-drill-open-tab">
                <ExternalLink className="w-3.5 h-3.5 mr-1" />
                Open in {spec.deepLinkLabel || 'Source Tab'}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={explainDrill}
              disabled={!rows.length || ai === 'loading'}
              className="text-white hover:opacity-90"
              style={{ background: '#F26B1F', borderColor: '#F26B1F' }}
              data-testid="pa-drill-explain">
              {ai === 'loading'
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Cognara…</>
                : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Explain (AI)</>}
            </Button>
            <Button size="sm" variant="outline" onClick={xlsxDownload}
              disabled={!rows.length || xlsxBusy} data-testid="pa-drill-xlsx">
              {xlsxBusy
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />}
              xlsx
            </Button>
            <Button size="sm" variant="outline" onClick={() => csvDownload(`${spec.exportName || 'drilldown'}.csv`, rows)}
              disabled={!rows.length} data-testid="pa-drill-csv">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="pa-drill-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Cognara AI panel */}
        {ai && ai !== 'loading' && (
          <div className="px-4 py-3 border-b" style={{ background: 'var(--tone-orange-bg)', borderColor: 'var(--tone-orange-bg)' }}
            data-testid="pa-drill-ai-panel">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <Sparkles className="w-4 h-4" style={{ color: '#F26B1F' }} />
                <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--tone-orange-text)' }}>
                  Cognara — {ai.source === 'cognara' ? 'AI analysis' : 'Heuristic fallback'}
                </div>
                <div className="text-[10px]" style={{ color: 'var(--tone-orange-text)' }}>
                  · analysed {ai.rowCount} record(s)
                </div>
              </div>
              <button onClick={() => setAi(null)}
                className="text-[10px] px-1.5 py-0.5 rounded hover:bg-orange-100"
                style={{ color: 'var(--tone-orange-text)' }}
                data-testid="pa-drill-ai-close">Dismiss</button>
            </div>
            <div className="mt-2 text-[11px] leading-relaxed whitespace-pre-wrap"
              style={{ color: 'var(--text-primary)', maxHeight: 260, overflowY: 'auto' }}>
              {ai.analysis}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-3">
          {loading && <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>}
          {!loading && rows.length === 0 && (
            <div className="p-10 text-center text-[12px] italic" style={{ color: C.slateMuted }}>
              No records match this filter.
            </div>
          )}
          {!loading && rows.length > 0 && (
            <table className="w-full text-[11px]">
              <thead style={{ background: C.bg, position: 'sticky', top: 0 }}>{headers}</thead>
              <tbody>
                {rows.map((r, i) => (
                  spec.shape === 'po' ? <RowPO key={r.poNumber + i} r={r} /> :
                  spec.shape === 'grn' ? <RowGRN key={(r.receiptNumber || '') + i} r={r} /> :
                  <RowMCI key={r.mciLineNumber + i} r={r} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Nested Vendor 360° popover */}
      {subVendor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: 'rgba(15,23,42,0.65)' }}
          onClick={() => setSubVendor(null)} data-testid="pa-vendor360">
          <div className="w-full max-w-3xl max-h-[85vh] rounded-xl shadow-2xl bg-[var(--card-bg)] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: C.border }}>
              <div>
                <div className="text-[10px] uppercase font-bold" style={{ color: C.slateMuted }}>Vendor 360°</div>
                <div className="text-base font-bold" style={{ color: C.slate }}>{subVendor}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setSubVendor(null)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {!subVendorData && <Loader2 className="w-5 h-5 animate-spin mx-auto" />}
              {subVendorData && (
                <>
                  <div className="grid grid-cols-4 gap-2">
                    <Kpi icon={Package} label="MCIs" value={fmtNum(subVendorData.mciCount)} color={C.primary} />
                    <Kpi icon={ClipboardCheck} label="POs" value={fmtNum(subVendorData.poCount)} color={C.accent} />
                    <Kpi icon={ClipboardCheck} label="Accepted" value={fmtNum(subVendorData.totals?.accepted)} color={C.success} />
                    <Kpi icon={AlertTriangle} label="Rejected" value={fmtNum(subVendorData.totals?.rejected)} color={C.danger} />
                  </div>
                  <div>
                    <div className="text-[9px] uppercase font-bold mb-1" style={{ color: C.slateMuted }}>Purchase Orders</div>
                    <table className="w-full text-[10px]">
                      <thead style={{ background: C.bg }}><tr>
                        <th className="p-1 text-left" style={{ color: C.slateMuted }}>PO#</th>
                        <th className="p-1 text-left" style={{ color: C.slateMuted }}>Date</th>
                        <th className="p-1 text-right" style={{ color: C.slateMuted }}>Value</th>
                        <th className="p-1 text-right" style={{ color: C.slateMuted }}>MCIs</th>
                      </tr></thead>
                      <tbody>
                        {(subVendorData.pos || []).map(p => (
                          <tr key={p.poNumber} style={{ borderTop: `1px solid ${C.border}` }}>
                            <td className="p-1 font-mono font-bold" style={{ color: C.primaryText }}>{p.poNumber}</td>
                            <td className="p-1">{fmtDate(p.poDate)}</td>
                            <td className="p-1 text-right font-mono">{fmtMoney(p.poValue)}</td>
                            <td className="p-1 text-right font-mono">{p.mciCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(subVendorData.receipts || []).length > 0 && (
                    <div>
                      <div className="text-[9px] uppercase font-bold mb-1" style={{ color: C.slateMuted }}>
                        Recent GRN Receipts
                      </div>
                      <table className="w-full text-[10px]">
                        <thead style={{ background: C.bg }}><tr>
                          <th className="p-1 text-left" style={{ color: C.slateMuted }}>GRN#</th>
                          <th className="p-1 text-left" style={{ color: C.slateMuted }}>Date</th>
                          <th className="p-1 text-left" style={{ color: C.slateMuted }}>MCI#</th>
                          <th className="p-1 text-right" style={{ color: C.slateMuted }}>Accepted</th>
                          <th className="p-1 text-right" style={{ color: C.slateMuted }}>Rejected</th>
                        </tr></thead>
                        <tbody>
                          {subVendorData.receipts.slice(0, 25).map((r, i) => (
                            <tr key={(r.receiptNumber || '') + i} style={{ borderTop: `1px solid ${C.border}` }}>
                              <td className="p-1 font-mono">{r.receiptNumber || '—'}</td>
                              <td className="p-1">{fmtDate(r.receiptDate)}</td>
                              <td className="p-1 font-mono">{r.mciLineNumber}</td>
                              <td className="p-1 text-right font-mono" style={{ color: C.success }}>{fmtNum(r.acceptedQty)}</td>
                              <td className="p-1 text-right font-mono" style={{ color: C.danger }}>{fmtNum(r.rejectedQty)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ============================================================================ */

export default function ProcurementAnalytics({ projectId }) {
  const [category, setCategory] = useState('BULK');
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const [data, setData] = useState({
    overview: null, progress: null, stageAging: null, materialAging: null,
    poAging: null, grnIqc: null, materialType: null, vendorHeatmap: null,
  });

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const qs = category ? `?category=${category}` : '';
      const [ov, pr, sa, ma, pa, gi, mt, vh] = await Promise.all([
        api.get(`/v1/projects/${projectId}/mci/analytics/overview${qs}`),
        api.get(`/v1/projects/${projectId}/mci/analytics/progress${qs}`),
        api.get(`/v1/projects/${projectId}/mci/analytics/stage-aging${qs}`),
        api.get(`/v1/projects/${projectId}/mci/analytics/material-aging${qs}`),
        api.get(`/v1/projects/${projectId}/mci/analytics/po-aging${qs}`),
        api.get(`/v1/projects/${projectId}/mci/analytics/grn-iqc${qs}`),
        api.get(`/v1/projects/${projectId}/mci/analytics/material-type${qs}`),
        api.get(`/v1/projects/${projectId}/mci/analytics/vendor-heatmap${qs}`),
      ]);
      setData({
        overview: ov.data, progress: pr.data, stageAging: sa.data,
        materialAging: ma.data, poAging: pa.data, grnIqc: gi.data,
        materialType: mt.data, vendorHeatmap: vh.data,
      });
    } catch (e) { toast.error('Failed to load analytics'); }
    setLoading(false);
  }, [projectId, category]);
  useEffect(() => { load(); }, [load]);

  /* --------------- drilldown launchers --------------- */
  const base = `/v1/projects/${projectId}/mci/analytics`;
  const qs = (extra = {}) => new URLSearchParams({ category, ...extra }).toString();

  const openProgress = (bucket) => setDrill({
    eyebrow: 'Progress Analytics',
    title: `MCIs in the ${bucket} completion bucket`,
    subtitle: `Category · ${category}`,
    url: `${base}/progress-drilldown?${qs({ bucket })}`,
    shape: 'mci', rowsKey: 'mcis',
    exportName: `progress-${bucket}-${category.toLowerCase()}`,
    deepLinkTab: 'register', deepLinkLabel: 'MCI Register',
    category,
  });

  const openStage = (gate, slice) => setDrill({
    eyebrow: 'Aging by Stage Gate',
    title: `${gate} · ${slice === 'open' ? 'Open MCIs (no actual date)' : 'MCIs with measured lead-time'}`,
    subtitle: `Category · ${category}`,
    url: `${base}/stage-drilldown?${qs({ gate, slice })}`,
    shape: 'mci', rowsKey: 'mcis',
    exportName: `stage-${gate}-${slice}-${category.toLowerCase()}`,
    deepLinkTab: 'register', deepLinkLabel: 'MCI Register',
    category,
  });

  const openMaterial = (code) => setDrill({
    eyebrow: 'Material Code',
    title: `Material Code · ${code}`,
    subtitle: `Category · ${category}`,
    url: `${base}/material-drilldown?${qs({ material_code: code })}`,
    shape: 'mci', rowsKey: 'mcis',
    exportName: `material-${code}-${category.toLowerCase()}`,
    deepLinkTab: 'register', deepLinkLabel: 'MCI Register',
    category,
  });

  const openDiscipline = (discipline, mode) => setDrill({
    eyebrow: mode === 'spend' ? 'Spend by Discipline' : 'Aging by Discipline',
    title: `${discipline} · ${mode === 'spend' ? 'Highest PO value first' : 'Oldest open MCIs first'}`,
    subtitle: `Category · ${category}`,
    url: `${base}/discipline-drilldown?${qs({ discipline, mode })}`,
    shape: 'mci', rowsKey: 'mcis',
    exportName: `discipline-${discipline}-${mode}-${category.toLowerCase()}`,
    deepLinkTab: 'register', deepLinkLabel: 'MCI Register',
    category,
  });

  const openPo = (slice) => setDrill({
    eyebrow: 'PO Aging & Analytics',
    title: slice === 'slow' ? 'Slowest POs (highest lead-time)' :
           slice === 'ontime' ? 'On-time POs (GRN before required date)' :
           slice === 'late' ? 'Late POs (missed required-at-site date)' :
           'All POs',
    subtitle: `Category · ${category}`,
    url: `${base}/po-drilldown?${qs({ slice })}`,
    shape: 'po', rowsKey: 'pos',
    exportName: `po-${slice}-${category.toLowerCase()}`,
    deepLinkTab: 'po', deepLinkLabel: 'PO Performance',
    category,
  });

  const openGrn = (status, vendor) => setDrill({
    eyebrow: 'GRN & IQC Analytics',
    title: `Receipts · ${status.toUpperCase()}${vendor ? ` · ${vendor}` : ''}`,
    subtitle: `Category · ${category}`,
    url: `${base}/grn-drilldown?${qs({ status, ...(vendor ? { vendor } : {}) })}`,
    shape: 'grn', rowsKey: 'receipts',
    exportName: `grn-${status}${vendor ? '-' + vendor.replace(/\s+/g, '_') : ''}-${category.toLowerCase()}`,
    deepLinkTab: 'ct-post', deepLinkLabel: 'Control Tower',
    category,
  });

  const openVendorMCI = (vendor) => setDrill({
    eyebrow: 'Vendor 360°',
    title: `Open MCIs · ${vendor}`,
    subtitle: `Category · ${category}`,
    url: `${base}/vendor-drilldown?${qs({ vendors: vendor })}`,
    shape: 'mci', rowsKey: 'mcis',
    exportName: `vendor-${vendor.replace(/\s+/g, '_')}-${category.toLowerCase()}`,
    deepLinkTab: 'vendors', deepLinkLabel: 'Vendors',
    category,
  });

  const openHeatmap = (qaBand, dlBand, vendors) => setDrill({
    eyebrow: 'Vendor Performance Heat-map',
    title: `QA ${qaBand} × Delivery ${dlBand} · ${vendors.length} vendor(s)`,
    subtitle: vendors.map(v => v.vendor).join(' · '),
    url: `${base}/vendor-drilldown?${qs({ vendors: vendors.map(v => v.vendor).join(',') })}`,
    shape: 'mci', rowsKey: 'mcis',
    exportName: `heatmap-${qaBand}-${dlBand}-${category.toLowerCase()}`,
    deepLinkTab: 'vendors', deepLinkLabel: 'Vendors',
    category,
  });

  /* --------------- render --------------- */
  const ov = data.overview || {};
  const pr = data.progress || {};
  const sa = data.stageAging || {};
  const ma = data.materialAging || {};
  const pa = data.poAging || {};
  const gi = data.grnIqc || {};
  const mt = data.materialType || {};
  const maxBucket = Math.max(1, ...(pr.distribution || []).map(b => b.count));
  const maxDisc = Math.max(1, ...(mt.byDiscipline || []).map(d => d.valueUsd || 0));
  const maxAging = Math.max(1, ...(ma.byDiscipline || []).map(r => r.avgDays || 0));

  return (
    <div className="space-y-3" data-testid="procurement-analytics">
      {/* Sub-tab picker */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 p-1 rounded-lg" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
          {['BULK', 'TAGGED'].map(c => (
            <button key={c} onClick={() => setCategory(c)}
              data-testid={`pa-subtab-${c.toLowerCase()}`}
              className="px-4 py-1.5 rounded text-xs font-bold transition-colors"
              style={{
                background: category === c ? C.primaryText : 'transparent',
                color: category === c ? '#fff' : C.slate,
              }}>{c} Material</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={async () => {
              try {
                const res = await api.get(`${base}/export.xlsx?category=${category}`, { responseType: 'blob' });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement('a'); a.href = url;
                a.download = `procurement-analytics-${category.toLowerCase()}.xlsx`; a.click();
                URL.revokeObjectURL(url);
                toast.success('Analytics workbook downloaded');
              } catch { toast.error('Export failed'); }
            }} data-testid="pa-export-xlsx">
            <Package className="w-3.5 h-3.5 mr-1" /> Export xlsx
          </Button>
          <Button size="sm" variant="ghost" onClick={load} data-testid="pa-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {loading && <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}

      {!loading && (
        <>
          {/* KPI band — clickable */}
          <div className="grid grid-cols-6 gap-2" data-testid="pa-kpi-band">
            <Kpi icon={Package}       label="Total MCIs"     value={fmtNum(ov.totalMcis)}   color={C.primary}  testId="pa-kpi-total"     onClick={() => openProgress('0-24%')} />
            <Kpi icon={ClipboardCheck} label="Completed"      value={fmtNum(ov.completed)}   color={C.success}  testId="pa-kpi-completed" onClick={() => openProgress('100%')} />
            <Kpi icon={TrendingUp}     label="Avg Progress"   value={`${ov.avgProgressPct || 0}%`} color={C.accent} testId="pa-kpi-progress" onClick={() => openProgress('25-49%')} />
            <Kpi icon={Clock}          label="Pre-Order"      value={fmtNum(ov.preOrder)}    color={C.warn}     testId="pa-kpi-pre"       onClick={() => openStage('MR', 'open')} />
            <Kpi icon={Clock}          label="Post-Order"     value={fmtNum(ov.postOrder)}   color={C.accent}   testId="pa-kpi-post"      onClick={() => openStage('PO', 'sample')} />
            <Kpi icon={DollarSign}     label="Total Value"    value={fmtMoney(ov.totalValueUsd)} color={C.primary} testId="pa-kpi-value"  onClick={() => openPo('all')} />
          </div>

          {/* 1. Progress distribution — every bucket clicks */}
          <Section icon={TrendingUp} title="Progress Analytics"
            hint="Click any bucket to see the MCIs in it" testId="pa-progress-section">
            <div className="space-y-1.5">
              {(pr.distribution || []).map(b => (
                <button key={b.bucket} onClick={() => openProgress(b.bucket)}
                  disabled={!b.count}
                  className={`w-full grid grid-cols-6 gap-2 items-center p-1 rounded text-left ${b.count ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default opacity-60'}`}
                  data-testid={`pa-progress-bucket-${b.bucket}`}>
                  <div className="text-[10px] font-mono col-span-1" style={{ color: C.slateMuted }}>{b.bucket}</div>
                  <div className="col-span-5">
                    <Bar pct={b.count / maxBucket * 100}
                      color={b.bucket === '100%' ? C.success : b.bucket.startsWith('0') ? C.danger : C.primary}
                      label={`${b.count} MCIs`} />
                  </div>
                </button>
              ))}
            </div>
          </Section>

          {/* 2. Stage aging table — every gate + open count clicks */}
          <Section icon={Clock} title="Aging by Stage Gate"
            hint="Click any Sample or Open cell to drill" testId="pa-stage-aging-section">
            <div className="overflow-hidden rounded-md border" style={{ borderColor: C.border }}>
              <table className="w-full text-[11px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <thead>
                  <tr style={{ background: C.bg }}>
                    <th className="px-3 py-2 text-left font-semibold uppercase text-[9px] tracking-wide" style={{ color: C.slateMuted, width: '60px' }}>Gate</th>
                    <th className="px-3 py-2 text-right font-semibold uppercase text-[9px] tracking-wide" style={{ color: C.slateMuted, width: '90px' }}>Sample</th>
                    <th className="px-3 py-2 text-right font-semibold uppercase text-[9px] tracking-wide" style={{ color: C.slateMuted, width: '110px' }}>Median (d)</th>
                    <th className="px-3 py-2 text-right font-semibold uppercase text-[9px] tracking-wide" style={{ color: C.slateMuted, width: '80px' }}>p90</th>
                    <th className="px-3 py-2 text-right font-semibold uppercase text-[9px] tracking-wide" style={{ color: C.slateMuted, width: '80px' }}>Max</th>
                    <th className="px-3 py-2 text-right font-semibold uppercase text-[9px] tracking-wide" style={{ color: C.slateMuted, width: '90px' }}>Open Now</th>
                  </tr>
                </thead>
                <tbody>
                  {(sa.gates || []).map((g, i) => (
                    <tr key={g.gate}
                      className="transition-colors"
                      style={{
                        background: i % 2 ? 'var(--tone-slate-bg)' : '#FFFFFF',
                        borderTop: `1px solid ${C.border}`,
                      }}
                      data-testid={`pa-stage-${g.gate}`}>
                      <td className="px-3 py-2 font-mono font-bold" style={{ color: C.primaryText }}>{g.gate}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {g.sampleSize ? (
                          <button onClick={() => openStage(g.gate, 'sample')}
                            className="px-2 py-0.5 rounded font-bold hover:bg-blue-50 transition-colors"
                            style={{ color: C.primaryText }}
                            data-testid={`pa-stage-${g.gate}-sample`}>
                            {g.sampleSize}
                          </button>
                        ) : <span style={{ color: '#CBD5E1' }}>0</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: C.slate }}>
                        {g.medianDays ?? <span style={{ color: '#CBD5E1' }}>—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: C.slate }}>
                        {g.p90Days ?? <span style={{ color: '#CBD5E1' }}>—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: g.maxDays > 30 ? C.danger : C.slate }}>
                        {g.maxDays ?? <span style={{ color: '#CBD5E1' }}>—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {g.openCount ? (
                          <button onClick={() => openStage(g.gate, 'open')}
                            className="px-2 py-0.5 rounded font-bold hover:bg-orange-50 transition-colors"
                            style={{ color: C.warn, background: 'var(--tone-orange-bg)' }}
                            data-testid={`pa-stage-${g.gate}-open`}>
                            {g.openCount}
                          </button>
                        ) : <span style={{ color: '#CBD5E1' }}>0</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* 3. Material Aging by Discipline + top offenders (drill-able) */}
          <Section icon={AlertTriangle} title="Aging by Material Type"
            hint="Click any discipline bar or top-offender card to drill" testId="pa-material-aging-section">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[9px] uppercase font-bold mb-1" style={{ color: C.slateMuted }}>Avg MR→Site by Discipline</div>
                <div className="space-y-1">
                  {(ma.byDiscipline || []).map(r => (
                    <button key={r.discipline} onClick={() => openDiscipline(r.discipline, 'aging')}
                      className="w-full grid grid-cols-4 gap-1 items-center p-1 rounded text-left hover:bg-slate-50"
                      data-testid={`pa-mat-aging-disc-${r.discipline}`}>
                      <div className="text-[10px] col-span-1 truncate" style={{ color: C.slate }}>{r.discipline}</div>
                      <div className="col-span-3">
                        <Bar pct={(r.avgDays / maxAging) * 100}
                          color={r.avgDays > 60 ? C.danger : r.avgDays > 30 ? C.warn : C.success}
                          label={`${r.avgDays}d · ${r.opens} open`} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase font-bold mb-1" style={{ color: C.slateMuted }}>Top Offenders — click to drill</div>
                <div className="max-h-56 overflow-y-auto space-y-1">
                  {(ma.topOffenders || []).slice(0, 8).map(o => (
                    <button key={o.mciLineNumber} onClick={() => openMaterial(o.materialCode)}
                      className="w-full text-left p-1.5 rounded border hover:bg-slate-50 text-[10px] flex items-center justify-between"
                      style={{ borderColor: C.border }} data-testid={`pa-offender-${o.mciLineNumber}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-mono font-bold" style={{ color: C.primaryText }}>{o.mciLineNumber}</div>
                        <div className="truncate" style={{ color: C.slate }}>{o.description}</div>
                      </div>
                      <div className="text-right ml-2">
                        <div className="font-mono font-bold" style={{ color: C.danger }}>{o.ageDays}d</div>
                        <ChevronRight className="w-3 h-3 inline" style={{ color: C.slateMuted }} />
                      </div>
                    </button>
                  ))}
                  {(ma.topOffenders || []).length === 0 && (
                    <div className="text-[10px] italic p-2 text-center" style={{ color: C.slateMuted }}>No aged items — everything on schedule.</div>
                  )}
                </div>
              </div>
            </div>
          </Section>

          {/* 4. PO Aging — all four KPIs + slowest rows clickable */}
          <Section icon={ClipboardCheck} title="PO Aging & Analytics"
            hint="Click any KPI tile or PO row to drill" testId="pa-po-aging-section">
            <div className="grid grid-cols-4 gap-2 mb-2">
              <Kpi icon={Clock} label="Median PO→GRN" value={pa.medianLeadDays ? `${pa.medianLeadDays}d` : '—'}
                color={C.primary} testId="pa-po-median" onClick={() => openPo('all')} />
              <Kpi icon={Clock} label="p90 PO→GRN" value={pa.p90LeadDays ? `${pa.p90LeadDays}d` : '—'}
                color={C.warn} testId="pa-po-p90" onClick={() => openPo('slow')} />
              <Kpi icon={ClipboardCheck} label="On-Time %" value={pa.onTimeDeliveryPct != null ? `${pa.onTimeDeliveryPct}%` : '—'}
                color={(pa.onTimeDeliveryPct || 0) >= 90 ? C.success : (pa.onTimeDeliveryPct || 0) >= 70 ? C.warn : C.danger}
                testId="pa-po-ontime" onClick={() => openPo('late')} />
              <Kpi icon={Package} label="Sample POs" value={fmtNum(pa.sampleSize)}
                color={C.slate} testId="pa-po-sample" onClick={() => openPo('all')} />
            </div>
            <div className="text-[9px] uppercase font-bold mb-1" style={{ color: C.slateMuted }}>Top-5 Slowest POs · click any row</div>
            <table className="w-full text-[11px]">
              <thead style={{ background: C.bg }}>
                <tr>
                  <th className="p-1.5 text-left" style={{ color: C.slateMuted }}>PO</th>
                  <th className="p-1.5 text-left" style={{ color: C.slateMuted }}>Vendor</th>
                  <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>Lead time</th>
                  <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {(pa.slowestPos || []).map(p => (
                  <tr key={p.poNumber} className="cursor-pointer hover:bg-slate-50"
                    onClick={() => openVendorMCI(p.vendor)}
                    style={{ borderTop: `1px solid ${C.border}` }} data-testid={`pa-slow-po-${p.poNumber}`}>
                    <td className="p-1.5 font-mono font-bold" style={{ color: C.primaryText }}>{p.poNumber}</td>
                    <td className="p-1.5" style={{ color: C.slate }}>{p.vendor}</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: C.danger }}>{p.leadTimeDays}d</td>
                    <td className="p-1.5 text-right font-mono">{fmtMoney(p.poValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* 5. GRN + IQC — every tile + vendor row clickable */}
          <Section icon={ClipboardCheck} title="GRN & IQC Analytics"
            hint="Click any KPI or reject-vendor row to drill into receipts" testId="pa-grn-iqc-section">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <Kpi icon={ClipboardCheck} label="Accepted" value={fmtNum(gi?.totals?.accepted)} color={C.success}
                    testId="pa-grn-accepted" onClick={() => openGrn('accepted')} />
                  <Kpi icon={Clock} label="Pending IQC" value={fmtNum(gi?.totals?.pending)} color={C.warn}
                    testId="pa-grn-pending" onClick={() => openGrn('pending')} />
                  <Kpi icon={AlertTriangle} label="Rejected" value={fmtNum(gi?.totals?.rejected)} color={C.danger}
                    testId="pa-grn-rejected" onClick={() => openGrn('rejected')} />
                </div>
                <div className="text-[9px] uppercase font-bold mb-1" style={{ color: C.slateMuted }}>Top Rejection Reasons</div>
                <div className="space-y-1">
                  {(gi.topRejectionReasons || []).slice(0, 5).map(r => (
                    <button key={r.reason} onClick={() => openGrn('rejected')}
                      className="w-full flex items-center justify-between text-[10px] p-1 rounded hover:bg-slate-100"
                      style={{ background: C.bg }} data-testid={`pa-reject-reason-${r.reason}`}>
                      <span style={{ color: C.slate }}>{r.reason}</span>
                      <span className="font-mono font-bold" style={{ color: C.danger }}>{r.count}</span>
                    </button>
                  ))}
                  {(gi.topRejectionReasons || []).length === 0 && (
                    <div className="text-[10px] italic p-1" style={{ color: C.slateMuted }}>No rejections recorded.</div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase font-bold mb-1" style={{ color: C.slateMuted }}>Highest Reject-Rate Vendors · click any row</div>
                <table className="w-full text-[10px]">
                  <thead style={{ background: C.bg }}><tr>
                    <th className="p-1 text-left" style={{ color: C.slateMuted }}>Vendor</th>
                    <th className="p-1 text-right" style={{ color: C.slateMuted }}>Rej %</th>
                    <th className="p-1 text-right" style={{ color: C.slateMuted }}>Rcpts</th>
                  </tr></thead>
                  <tbody>
                    {(gi.topRejectVendors || []).slice(0, 8).map(v => (
                      <tr key={v.vendor} className="cursor-pointer hover:bg-slate-50"
                        onClick={() => openGrn('rejected', v.vendor)}
                        style={{ borderTop: `1px solid ${C.border}` }} data-testid={`pa-reject-vendor-${v.vendor}`}>
                        <td className="p-1" style={{ color: C.slate }}>{v.vendor}</td>
                        <td className="p-1 text-right font-mono font-bold"
                          style={{ color: v.rejectRatePct > 10 ? C.danger : v.rejectRatePct > 3 ? C.warn : C.success }}>
                          {v.rejectRatePct}%
                        </td>
                        <td className="p-1 text-right font-mono">{v.receipts}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Section>

          {/* 6. Material Type Analytics — spend bar + top-10 clickable */}
          <Section icon={DollarSign} title="Material Type Analytics"
            hint="Click any discipline bar or material code row to drill" testId="pa-material-type-section">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[9px] uppercase font-bold mb-1" style={{ color: C.slateMuted }}>Spend by Discipline</div>
                <div className="space-y-1">
                  {(mt.byDiscipline || []).map(d => (
                    <button key={d.discipline} onClick={() => openDiscipline(d.discipline, 'spend')}
                      className="w-full grid grid-cols-4 gap-1 items-center p-1 rounded text-left hover:bg-slate-50"
                      data-testid={`pa-spend-disc-${d.discipline}`}>
                      <div className="text-[10px] col-span-1 truncate" style={{ color: C.slate }}>{d.discipline}</div>
                      <div className="col-span-3">
                        <Bar pct={(d.valueUsd / maxDisc) * 100} color={C.primary} label={fmtMoney(d.valueUsd)} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase font-bold mb-1" style={{ color: C.slateMuted }}>Top-10 Material Codes by Value · click any</div>
                <div className="max-h-56 overflow-y-auto space-y-0.5">
                  {(mt.topByValue || []).map(m => (
                    <button key={m.materialCode} onClick={() => openMaterial(m.materialCode)}
                      className="w-full text-left p-1.5 rounded border hover:bg-slate-50 text-[10px] flex items-center justify-between"
                      style={{ borderColor: C.border }} data-testid={`pa-top-material-${m.materialCode}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-mono font-bold" style={{ color: C.primaryText }}>{m.materialCode}</div>
                        <div className="truncate" style={{ color: C.slate }}>{m.description}</div>
                      </div>
                      <div className="text-right ml-2">
                        <div className="font-mono font-bold" style={{ color: C.accent }}>{fmtMoney(m.totalValue)}</div>
                        <div style={{ color: C.slateMuted }}>{m.mciCount} MCIs</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Section>

          {/* 7. Vendor Performance Heat-map — square cells with explicit N/A column */}
          {(() => {
            const vh = data.vendorHeatmap || {};
            const vendors = vh.vendors || [];
            const dlBands = ['LOW', 'MED', 'HIGH', 'N/A'];
            const qaBands = ['HIGH', 'MED', 'LOW', 'N/A'];
            const grid = {};
            qaBands.forEach(qa => dlBands.forEach(dl => { grid[`${qa}|${dl}`] = []; }));
            vendors.forEach(v => {
              const key = `${v.qaBand}|${v.deliveryBand}`;
              if (grid[key] !== undefined) grid[key].push(v);
              else grid['N/A|N/A'].push(v);
            });
            const styleFor = (qa, dl) => {
              if (qa === 'N/A' || dl === 'N/A')
                return { bg: 'var(--tone-slate-bg)', border: '#CBD5E1', text: 'var(--text-muted)' };
              if (qa === 'HIGH' && dl === 'HIGH')
                return { bg: 'var(--tone-green-bg)', border: '#10B981', text: 'var(--tone-green-text)' };
              if (qa === 'LOW' || dl === 'LOW')
                return { bg: 'var(--tone-red-bg)', border: '#EF4444', text: 'var(--tone-red-text)' };
              return { bg: 'var(--tone-amber-bg)', border: '#F59E0B', text: 'var(--tone-amber-text)' };
            };
            return (
              <Section icon={AlertTriangle} title="Vendor Performance Heat-map"
                hint="On-time delivery × QA-accept rate — click any cell or row to drill"
                testId="pa-vendor-heatmap-section">
                {/* Legend */}
                <div className="flex items-center gap-3 mb-3 text-[10px]" style={{ color: C.slateMuted }}>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: 'var(--tone-green-bg)', border: '1.5px solid #10B981' }} /> Strategic (keep)</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: 'var(--tone-amber-bg)', border: '1.5px solid #F59E0B' }} /> Monitor</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: 'var(--tone-red-bg)', border: '1.5px solid #EF4444' }} /> Escalate / replace</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: 'var(--card-bg-alt)', border: '1.5px solid #CBD5E1' }} /> Insufficient data</span>
                </div>

                <div className="flex items-stretch gap-2">
                  {/* Left Y-axis label */}
                  <div className="flex items-center" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                    <span className="text-[9px] uppercase font-bold tracking-widest" style={{ color: C.slateMuted }}>
                      QA Accept Rate →
                    </span>
                  </div>

                  {/* Grid */}
                  <div className="flex-1">
                    <div className="grid gap-2" style={{ gridTemplateColumns: 'auto repeat(4, minmax(0, 1fr))' }}>
                      {/* header row */}
                      <div />
                      {dlBands.map(dl => (
                        <div key={dl} className="text-center text-[9px] uppercase font-bold tracking-wide pb-1"
                          style={{ color: C.slateMuted }}>
                          Delivery {dl}
                        </div>
                      ))}
                      {qaBands.map(qa => (
                        <Fragment key={qa}>
                          <div
                            className="text-[9px] uppercase font-bold tracking-wide flex items-center justify-end pr-2"
                            style={{ color: C.slateMuted }}>QA {qa}</div>
                          {dlBands.map(dl => {
                            const list = grid[`${qa}|${dl}`] || [];
                            const s = styleFor(qa, dl);
                            return (
                              <div key={`${qa}|${dl}`}
                                onClick={() => list.length && openHeatmap(qa, dl, list)}
                                className="rounded-lg p-2 transition-all"
                                style={{
                                  background: s.bg,
                                  border: `1.5px solid ${s.border}`,
                                  cursor: list.length ? 'pointer' : 'default',
                                  minHeight: 96,
                                  opacity: list.length ? 1 : 0.5,
                                }}
                                onMouseEnter={e => list.length && (e.currentTarget.style.transform = 'translateY(-2px)')}
                                onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
                                data-testid={`pa-heatmap-${qa}-${dl}`}>
                                <div className="flex items-baseline justify-between mb-1">
                                  <div className="text-2xl font-bold font-mono leading-none" style={{ color: s.text }}>
                                    {list.length}
                                  </div>
                                  {list.length > 0 && <ChevronRight className="w-3 h-3" style={{ color: s.text, opacity: 0.6 }} />}
                                </div>
                                <div className="text-[9px] space-y-0.5 max-h-12 overflow-y-auto"
                                  style={{ color: s.text }}>
                                  {list.slice(0, 3).map(v => (
                                    <div key={v.vendor} className="truncate font-medium relative group/vhov"
                                      onClick={(ev) => { ev.stopPropagation(); openVendorMCI(v.vendor); }}
                                      style={{ cursor: 'pointer' }}>
                                      <span className="underline decoration-dotted underline-offset-2">{v.vendor}</span>
                                      {/* Vendor 360° hover tooltip */}
                                      <div className="hidden group-hover/vhov:block absolute z-50 left-full ml-2 top-0 w-56 p-2 rounded-lg shadow-xl border text-left"
                                        style={{ background: 'var(--card-bg)', borderColor: C.border, color: C.slate }}>
                                        <div className="text-[10px] font-bold mb-1" style={{ color: C.primaryText }}>{v.vendor}</div>
                                        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[9px]">
                                          <span style={{ color: C.slateMuted }}>Risk</span>
                                          <span className="font-mono font-bold text-right" style={{
                                            color: v.risk === 'HIGH' ? C.danger : v.risk === 'MEDIUM' ? C.warn :
                                                   v.risk === 'LOW' ? C.success : C.slateMuted }}>{v.risk}</span>
                                          <span style={{ color: C.slateMuted }}>On-Time %</span>
                                          <span className="font-mono text-right">{v.onTimeDeliveryPct != null ? `${v.onTimeDeliveryPct}%` : '—'}</span>
                                          <span style={{ color: C.slateMuted }}>QA %</span>
                                          <span className="font-mono text-right">{v.qaAcceptPct != null ? `${v.qaAcceptPct}%` : '—'}</span>
                                          <span style={{ color: C.slateMuted }}>POs</span>
                                          <span className="font-mono text-right">{v.poCount}</span>
                                          <span style={{ color: C.slateMuted }}>MCIs</span>
                                          <span className="font-mono text-right">{v.mciCount}</span>
                                          <span style={{ color: C.slateMuted }}>PO Value</span>
                                          <span className="font-mono text-right" style={{ color: C.primaryText }}>{fmtMoney(v.poValueUsd)}</span>
                                          <span style={{ color: C.slateMuted }}>On-time / Late</span>
                                          <span className="font-mono text-right">
                                            <span style={{ color: C.success }}>{v.onTime}</span>
                                            <span style={{ color: C.slateMuted }}> / </span>
                                            <span style={{ color: C.danger }}>{v.late}</span>
                                          </span>
                                          <span style={{ color: C.slateMuted }}>Accept / Reject</span>
                                          <span className="font-mono text-right">
                                            <span style={{ color: C.success }}>{fmtNum(v.accepted)}</span>
                                            <span style={{ color: C.slateMuted }}> / </span>
                                            <span style={{ color: C.danger }}>{fmtNum(v.rejected)}</span>
                                          </span>
                                        </div>
                                        <div className="mt-1.5 pt-1.5 border-t text-[9px] italic" style={{ borderColor: C.border, color: C.slateMuted }}>
                                          Click for full vendor drilldown →
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  {list.length > 3 && (
                                    <div className="italic" style={{ opacity: 0.7 }}>+{list.length - 3} more</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </Fragment>
                      ))}
                    </div>
                    <div className="text-center text-[9px] uppercase font-bold tracking-widest mt-2"
                      style={{ color: C.slateMuted }}>On-Time Delivery →</div>
                  </div>
                </div>

                {/* Full risk table — every row clickable to vendor 360° */}
                <div className="mt-3 max-h-64 overflow-y-auto rounded-md border" style={{ borderColor: C.border }}>
                  <table className="w-full text-[10px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead style={{ background: C.bg, position: 'sticky', top: 0 }}>
                      <tr>
                        <th className="p-1.5 text-left font-semibold uppercase text-[9px] tracking-wide" style={{ color: C.slateMuted }}>Vendor</th>
                        <th className="p-1.5 text-left font-semibold uppercase text-[9px] tracking-wide" style={{ color: C.slateMuted }}>Risk</th>
                        <th className="p-1.5 text-right font-semibold uppercase text-[9px] tracking-wide" style={{ color: C.slateMuted }}>On-Time %</th>
                        <th className="p-1.5 text-right font-semibold uppercase text-[9px] tracking-wide" style={{ color: C.slateMuted }}>QA %</th>
                        <th className="p-1.5 text-right font-semibold uppercase text-[9px] tracking-wide" style={{ color: C.slateMuted }}>POs</th>
                        <th className="p-1.5 text-right font-semibold uppercase text-[9px] tracking-wide" style={{ color: C.slateMuted }}>PO Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendors.map((v, i) => (
                        <tr key={v.vendor} className="cursor-pointer hover:bg-blue-50 transition-colors"
                          onClick={() => openVendorMCI(v.vendor)}
                          style={{ borderTop: `1px solid ${C.border}`, background: i % 2 ? 'var(--tone-slate-bg)' : '#FFFFFF' }}
                          data-testid={`pa-heatmap-row-${v.vendor}`}>
                          <td className="p-1.5 font-medium" style={{ color: C.slate }}>{v.vendor}</td>
                          <td className="p-1.5">
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{
                              background: v.risk === 'HIGH' ? 'var(--tone-red-bg)' : v.risk === 'MEDIUM' ? 'var(--tone-amber-bg)' : v.risk === 'LOW' ? 'var(--tone-green-bg)' : 'var(--tone-slate-bg)',
                              color: v.risk === 'HIGH' ? 'var(--tone-red-text)' : v.risk === 'MEDIUM' ? 'var(--tone-amber-text)' : v.risk === 'LOW' ? 'var(--tone-green-text)' : 'var(--text-muted)',
                            }}>{v.risk}</span>
                          </td>
                          <td className="p-1.5 text-right font-mono" style={{ color: v.onTimeDeliveryPct == null ? '#CBD5E1' : C.slate }}>
                            {v.onTimeDeliveryPct != null ? `${v.onTimeDeliveryPct}%` : '—'}
                          </td>
                          <td className="p-1.5 text-right font-mono" style={{ color: v.qaAcceptPct == null ? '#CBD5E1' : C.slate }}>
                            {v.qaAcceptPct != null ? `${v.qaAcceptPct}%` : '—'}
                          </td>
                          <td className="p-1.5 text-right font-mono">{v.poCount}</td>
                          <td className="p-1.5 text-right font-mono">{fmtMoney(v.poValueUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            );
          })()}
        </>
      )}

      <DrilldownSheet open={!!drill} onClose={() => setDrill(null)} projectId={projectId} spec={drill} />
    </div>
  );
}
